import { Injectable, Injector, inject, signal } from '@angular/core';

import { Logger, NavigationService } from '@frontend/vanilla/core';
import { AssetsData } from '@payments/common-utils';
import { Subscription, interval } from 'rxjs';

import { ChannelType } from '../config/appConstants';
import { UrlConstants } from '../config/urlConstants';
import { DepositRedirectInfo } from '../model/common.model';
import { ResponseStatus } from '../shared/error.constants';
import { DataService } from './data.service';
import { LoaderService } from './loader.service';
import { QdApiService } from './qd.service';
import { ResizeService } from './resize.service';

@Injectable({
    providedIn: 'root',
})
export class DepositRedirectService {
    private injector = inject(Injector);
    private dataService = inject(DataService);
    private loaderService = inject(LoaderService);
    private apiService = inject(QdApiService);
    private resizer = inject(ResizeService);
    private navigationService = inject(NavigationService);
    private logger = inject(Logger);

    private defaultdepRedirectInfo = {
        submitCreq: false,
        submitJwt: false,
        creq: '',
        redirectionUrl: '',
        openInCustomTab: false,
        new_3ds2_route: false,
        redirectType: '',
    };
    private _depRedirectSignal = signal<DepositRedirectInfo>(this.defaultdepRedirectInfo);
    depRedirectSignal = this._depRedirectSignal.asReadonly();

    private _popupBlocked = signal<boolean>(false);
    popupBlocked = this._popupBlocked.asReadonly();
    updatePopupStatus(value: boolean) {
        this._popupBlocked.set(value);
    }
    resetdepositRedirectInfo() {
        this.depositSubmitResponse = null;
        this._depRedirectSignal.set(this.defaultdepRedirectInfo);
    }
    updateDespositRedirectInfo(updatedObject: Partial<DepositRedirectInfo>) {
        this._depRedirectSignal.update((currentObject) => ({
            ...currentObject,
            ...updatedObject,
        }));
    }
    depositSubmitResponse: any;
    intervalTimer: Subscription;
    callback: any;
    redirectFormTimeout = 200;

    constructor() {
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    redirectToThirdParty(callback: any) {
        const assetsData = this.injector.get(AssetsData);
        if (assetsData?.assets['miscimages'] && assetsData?.assets['miscimages']?.redirectFormTimeout) {
            this.redirectFormTimeout = Number.parseInt(assetsData.assets['miscimages']?.redirectFormTimeout);
        }
        this.loaderService.isLoading.next(true);
        const target = 'TheWindow';
        let childWindow: any;
        let redirectionActionUrl = '';
        const buildRedirectionUrl = (formType: string, params: Record<string, string>) => {
            let url = `${this.navigationService.location.baseUrl()}/payments/PostDepositRedirection?formtype=${formType}`;
            Object.entries(params).forEach(([key, value]) => {
                url += `&${key}=${window.encodeURIComponent(value)}`;
            });
            return url;
        };
        const sessionInfo = this.dataService?.sessionInfo;
        if ([ChannelType.AN, ChannelType.IN, ChannelType.AW, ChannelType.IW, ChannelType.IH].includes(sessionInfo?.channelId)) {
            if (this.depRedirectSignal().creq) {
                redirectionActionUrl = buildRedirectionUrl('creq', {
                    creq: this.depRedirectSignal().creq,
                    redirectionUrl: this.depRedirectSignal().redirectionUrl,
                });
            } else if (this.depositSubmitResponse?.postData) {
                const { postData, redirectionBackUrl } = this.depositSubmitResponse;

                if (postData.JWT) {
                    redirectionActionUrl = buildRedirectionUrl('JWT', {
                        JWT: postData.JWT,
                        MD: postData?.MD,
                        redirectionUrl: this.depRedirectSignal().redirectionUrl,
                    });
                } else if (postData.PaReq) {
                    redirectionActionUrl = buildRedirectionUrl('PaReq', {
                        PaReq: postData.PaReq,
                        TermUrl: redirectionBackUrl,
                        MD: postData.MD,
                        redirectionUrl: this.depRedirectSignal().redirectionUrl,
                    });
                }
            }
        }
        if (this.depRedirectSignal().openInCustomTab && redirectionActionUrl !== '') {
            this.handleRedirection(redirectionActionUrl, this.dataService.sessionInfo);
            this.checkDepositStatus(this.depositSubmitResponse?.transactionId, callback);
        } else {
            const url = this.depRedirectSignal().redirectType === 'GET' ? this.depRedirectSignal().redirectionUrl : redirectionActionUrl;

            childWindow = window.open(url, target, 'status=0,title=0,height=600,width=800,scrollbars=yes');

            if (childWindow) {
                this._popupBlocked.set(false);
                setTimeout(() => {
                    if (this.depRedirectSignal().redirectType === 'POST' && redirectionActionUrl === '') {
                        (<HTMLFormElement>document.getElementById('depositpostredirectform')).submit();
                    }
                    childWindow.focus();
                    this.checkDepositStatus(this.depositSubmitResponse?.transactionId, callback);
                }, this.redirectFormTimeout);
            } else {
                this._popupBlocked.set(true);
                this.loaderService.isLoading.next(false);
                this.resizer.resizeCashier();
            }
        }
    }

    checkDepositStatus(transactionID: any, callback: any, childWindow: any = null) {
        this.callback = callback;
        this.intervalTimer = interval(3000).subscribe(() => {
            const pathParam = `${UrlConstants.DEPOSIT_STATUS_URI}/${transactionID}`;
            this.apiService.get(pathParam).subscribe(
                (response) => {
                    if (response?.status !== ResponseStatus.PENDING) {
                        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
                        if (response.status == ResponseStatus.SUCCESS) {
                            this.dataService._callSuccessApi = true;
                        }
                        this.closeWindowAndCleanup(childWindow);
                        callback(response);
                    }
                },
                (rejected) => {
                    this.closeWindowAndCleanup(childWindow);
                    callback(rejected);
                },
            );
        });
    }
    private handleRedirection(redirectionActionUrl: string, sessionInfo: any): void {
        if (this.depRedirectSignal().new_3ds2_route) {
            const url = this.getCustomUrl(sessionInfo?.channelId, redirectionActionUrl);
            if (url) {
                this.openInCustomTabUrl(url);
            }
        } else {
            this.openInCustomTabUrl(redirectionActionUrl);
        }
    }
    private openInCustomTabUrl(url: string): void {
        if (window.top) {
            window.top.location.href = url;
        }
    }
    private getCustomUrl(channelId: string, redirectionActionUrl: string): string {
        let url = '';
        if ([ChannelType.AN, ChannelType.AW].includes(channelId)) {
            url = 'cashierex://?openinBrowser=' + window.encodeURIComponent(redirectionActionUrl);
        } else if ([ChannelType.IN, ChannelType.IW, ChannelType.IH].includes(channelId)) {
            url = 'bwinex://?openinBrowser=' + window.encodeURIComponent(redirectionActionUrl);
        }
        return url;
    }
    private closeWindowAndCleanup(childWindow: any): void {
        if (childWindow) childWindow.close();
        this.intervalTimer.unsubscribe();
    }

    check3dDepositStatus(callback: any) {
        let is3dResponseReceived = false;
        const url = `${UrlConstants.THREEDSMETHOD_STATUS_URI}/${this.depositSubmitResponse?.transactionId}`;
        const intervalTimer: Subscription = interval(2000).subscribe(() => {
            this.apiService.get(url).subscribe(
                (data) => {
                    intervalTimer.unsubscribe();
                    is3dResponseReceived = true;
                    callback(data);
                },
                (error) => {
                    this.logger.error('Error loading 3ds:', error);
                },
            );
        });

        setTimeout(() => {
            if (!is3dResponseReceived) {
                intervalTimer.unsubscribe();
                callback();
            }
        }, 10000);
    }

    getRedirectData = (callback: any) => {
        const url = `${UrlConstants.REDIRECTION_DATA_URI}/${this.depositSubmitResponse?.transactionId}`;
        this.apiService.get(url).subscribe((response: any) => {
            callback(response);
        });
    };
    handleVisibilityChange() {
        if (document.visibilityState === 'hidden') {
            this.intervalTimer.unsubscribe();
        } else {
            this.intervalTimer.unsubscribe();
            this.checkDepositStatus(this.depositSubmitResponse?.transactionId, this.callback);
        }
    }
}
